import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';
import { WaveFile } from 'wavefile';

import { useMicEffect } from '@/composables/shared/mic/create/useMicEffect';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { DeviceMic, TypeInputMic } from '@/types/components';
import { AudioBufferMap, MediaStreamMap, MicContext, RecordingState } from '@/types/composables';
import { MessageFlag } from '@/types/socket';
import { controllGain } from '@/utils/deprecated/audioGainController.js';
import { getBrowserAndDeviceInfo } from '@/utils/device';

const voiceRecordingResult = ref(); // 음성 녹음 결과 (음성 녹음 시 wavBlob 담김)
const gainNode = ref(); // 마이크 입력 볼륨 조절용 GainNode

const micContextMap = ref<Record<TypeInputMic, MicContext | null>>({
  streaming: null,
  voice: null,
  voice_streaming: null,
}); // AudioContext 및 WorkletNode 저장소
const mediaStreamMap = ref<MediaStreamMap>({
  streaming: null,
  voice: null,
  voice_streaming: null,
}); // 실제 마이크 스트림 저장소
const audioBufferMap = ref<AudioBufferMap>({
  streaming: [],
  voice: [],
  voice_streaming: [],
}); // 오디오 버퍼 저장소
const isRecording = ref<RecordingState>({
  streaming: false,
  voice: false,
  voice_streaming: false,
}); // 현재 녹음 중인지 여부

const { browser } = getBrowserAndDeviceInfo();
const isSafari = browser === 'Safari';
const audioChunkSize = 5600;
const SAMPLE_RATE = 16000;

let isStreamThrottled = false;
let audioAnalyser;
let gainUpdateCounter = 0;
let intervalGainMonitor = 0;

export function useMic() {
  const audioStore = useAudioStore();
  const socketStore = useSocketStore();
  const toastStore = useToastStore();
  const { t } = useI18n();
  const { isMicOn, selectedInput } = storeToRefs(audioStore);

  // 마이크 권한 요청 및 입력장치 설정
  const micAccess = async (deviceId?: string) => {
    try {
      // 오디오 입력 장치 설정 (에코 제거, 노이즈 제거 등 비활성화)
      const audioConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      };
      if (deviceId) audioConstraints['deviceId'] = { exact: deviceId }; // 특정 장치 ID를 설정

      // 사용자 미디어 접근 요청 (마이크)
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: audioConstraints,
      });

      const track = mediaStream.getAudioTracks()[0]; // 오디오 트랙 가져오기
      let isTrackInvalid = false;

      // 트랙 유효성 검사
      if (!track) {
        console.warn('[F] 오디오 트랙이 반환되지 않았습니다.');
        isTrackInvalid = true;
      } else if (track.readyState !== 'live') {
        console.warn(
          `[F] 오디오 트랙이 활성 상태가 아닙니다: ${track.readyState}`
        );
        isTrackInvalid = true;
      } else if (!track.enabled) {
        console.warn('[F] 오디오 트랙이 비활성화되었습니다.');
        isTrackInvalid = true;
      }

      // 트랙이 유효하지 않은 경우 스트림 정리
      if (isTrackInvalid) {
        mediaStream.getTracks().forEach((t) => t.stop());
        return null;
      }

      console.log(
        `[F] 트랙 상태: enabled=${track.enabled}, muted=${track.muted}, readyState=${track.readyState}`
      );

      // 즉시 종료되는 트랙 감지
      if (!track || track.readyState === 'ended') {
        console.warn('[F] 마이크 접근 실패: 트랙이 즉시 종료되었습니다.');
        mediaStream.getTracks().forEach((t) => t.stop());
        return null;
      }

      // 트랙 종료 이벤트 핸들러
      track.onended = () => {
        console.error('[F] 마이크 트랙이 예기치 않게 종료되었습니다.');
      };

      // Safari 브라우저의 경우 추가 설정
      if (isSafari) {
        await audioStore.initDevices(); // 오디오 장치 초기화
        socketStore.sendMessageChangeDevice(selectedInput.value.voiceId); // WebSocket을 통해 장치 변경 메시지 전송
      }

      return mediaStream; // 유효한 미디어 스트림 반환
    } catch (err) {
      const e = err as DOMException;
      console.error('마이크 접근 실패:', e.name, e.message);
      return null;
    }
  };

  // 마이크 사용 준비 (컨텍스트 및 프로세서 세팅)
  const micPrep = async (
    type: TypeInputMic = 'streaming',
    input: DeviceMic | null = null,
    message: Record<string, any> = {}
  ): Promise<boolean> => {
    const device =
      !input || !input.id || input.id === '' || input.id === undefined
        ? selectedInput.value
        : input;

    if (isStreamThrottled) return false; // 스트림 제한 상태 확인
    isStreamThrottled = true;

    // 기존 컨텍스트 정리
    if (
      mediaStreamMap.value[type] !== null ||
      micContextMap.value[type] !== null
    ) {
      await micClear(type); // 기존 스트림 및 컨텍스트 정리
    }

    isRecording.value[type] = false; // 녹음 상태 초기화

    try {
      const newStream = await micAccess(device?.id); // 새로운 스트림 요청
      if (newStream !== null) {
        mediaStreamMap.value[type] = newStream; // 스트림 저장
      } else {
        isStreamThrottled = false;
        return false;
      }

      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE }); // 오디오 컨텍스트 생성

      try {
        await audioContext.resume(); // 오디오 컨텍스트 활성화
        if (audioContext.state !== 'running') {
          console.warn('[F] 오디오 컨텍스트가 활성화되지 않았습니다.');
          isStreamThrottled = false;
          await micClear(type);
          return false;
        }
      } catch (e) {
        console.warn('오디오 컨텍스트 활성화 실패:', e);
        isStreamThrottled = false;
        await micClear(type);
        return false;
      }

      // 오디오 워크렛 모듈 등록
      const modulePath =
        import.meta.env.BASE_URL === '/'
          ? `/js/processor.js`
          : `${import.meta.env.BASE_URL}/js/processor.js`;

      try {
        await audioContext.audioWorklet.addModule(modulePath);
      } catch (e) {
        console.error('오디오 워크렛 모듈 로드 실패', e);
        await micClear(type);
        isStreamThrottled = false;
        return false;
      }

      // 분석기 설정
      audioAnalyser = audioContext.createAnalyser();
      audioAnalyser.fftSize = 1024;

      // Gain 및 Merge 처리 포함한 destination 생성
      const destination = createDestinationWithGainAdjusted(
        mediaStreamMap.value[type],
        audioContext,
        device
      );

      if (destination === null) {
        await micClear(type);
        isStreamThrottled = false;
        return false;
      }

      // 증폭된 소스 생성
      const amplifiedSource = audioContext.createMediaStreamSource(
        destination.stream
      );
      setupMicEffect(audioContext, amplifiedSource); // 마이크 효과 설정

      // 오디오 워크렛 프로세서 생성
      const processor = new AudioWorkletNode(audioContext, 'audio-processor', {
        processorOptions: { bufferSize: audioChunkSize },
      });

      let workletStarted = false;
      let watchdogTimer: number;

      // 워크렛으로부터 받은 오디오 데이터 처리
      processor.port.onmessage = (event) => {
        workletStarted = true;
        clearTimeout(watchdogTimer); // 워크렛 시작 확인 타이머 해제

        if (!isRecording.value[type]) {
          return;
        }

        const audioData = event.data;
        audioBufferMap.value[type].push(...audioData);

        if (['streaming', 'voice_streaming'].includes(type)) {
          const flag: MessageFlag = {
            streaming: 'as',
            voice_streaming: 'vs',
          }[type];
          const isReady = {
            streaming: true,
            voice_streaming: ['INITIAL', 'VOICE_DETECTED'].includes(
              audioStore.voiceStatus
            ),
          }[type];

          if (audioBufferMap.value[type].length >= audioChunkSize && isReady) {
            const audioBytes = new Float32Array(audioBufferMap.value[type]);
            socketStore.sendMessageAudioStreamChunk(
              new Uint8Array(audioBytes.buffer),
              flag
            );
            audioBufferMap.value[type] = [];
          }

          gainUpdateCounter++;
          if (gainUpdateCounter % 4 === 0) controllGain(audioData); // Gain 조절
        }

        if (type === 'voice') controllGain(audioData, true); // 음성 녹음 Gain 조절
      };

      // 1.5초 후 워크렛이 시작되지 않으면 경고
      watchdogTimer = setTimeout(async () => {
        if (!workletStarted) {
          console.warn(
            '[F] AudioWorkletNode가 시작되지 않았습니다. 다시 시도합니다.'
          );
          await micClear(type);
          isStreamThrottled = false;
          setTimeout(() => micStart(type, input, message), 300); // soft retry
        }
      }, 1500);

      // 노드 연결
      amplifiedSource.connect(processor);
      amplifiedSource.connect(audioAnalyser);
      processor.connect(audioContext.destination);

      // 컨텍스트 저장
      micContextMap.value[type] = {
        audioContext,
        processor,
        stop: () => {
          isRecording.value[type] = false;
        },
        start: () => {
          isRecording.value[type] = true;
        },
      };

      isStreamThrottled = false;
      return true;
    } catch (err) {
      console.error(`마이크 접근 실패[${type}]:`, err);
      await micClear(type);
      isStreamThrottled = false;
      return false;
    }
  };

  // 녹음 시작
  const micStart = async (
    type: TypeInputMic = 'streaming',
    input: DeviceMic | null = null,
    message: Record<string, any> = {}
  ) => {
    // 마이크가 준비 안된 경우 준비 작업 수행
    if (!isMicReady(type)) {
      const success = await micPrep(type, input, message);
      if (!success) {
        isMicOn.value = false;
        toastStore.error(t('reject_mic'));
        return;
      }
    }

    await Promise.resolve();

    // 녹음 시작
    const context = micContextMap.value[type];
    if (context && !isRecording.value[type]) {
      context.start();
    }

    if (type === 'streaming') {
      socketStore.sendMessageAudioStreamStart();
    }

    if (type === 'voice_streaming') {
      isMicOn.value = true;
      socketStore.sendMessageVoiceStreamStart(message);
    }

    toastStore.info(t('toast_mic_on'), 'MIC');
    return;
  };

  // 녹음 중지
  const micStop = (
    type: TypeInputMic = 'streaming',
    message: Record<string, any> = {}
  ) => {
    if (type === 'streaming') {
      socketStore.sendMessageAudioStreamEnd();
      gainUpdateCounter = 0;
    }

    if (type === 'voice_streaming') {
      socketStore.sendMessageVoiceStreamEnd(message);
      gainUpdateCounter = 0;
    }

    if (type === 'voice') {
      // 녹음 결과 wav 파일 생성
      const audioFloat32Array = [...audioBufferMap.value['voice']];
      const wav = new WaveFile();
      wav.fromScratch(1, SAMPLE_RATE, '32f', audioFloat32Array);
      const wavBuffer = wav.toBuffer();
      const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

      voiceRecordingResult.value = {
        wavBlob,
        sampleRate: SAMPLE_RATE,
        durationSec: 5,
      };
    }

    audioBufferMap.value[type] = [];
    if (micContextMap.value[type]) {
      micContextMap.value[type].stop();
    }
    isRecording.value[type] = false;
    return true;
  };

  // 마이크 초기화
  const micClear = async (type: TypeInputMic) => {
    const stream = mediaStreamMap.value[type];
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      mediaStreamMap.value[type] = null;
    }

    const context = micContextMap.value[type];
    if (context) {
      if (context.processor) {
        context.processor.disconnect();
        context.processor.port.close();
      }

      const contextAudio = context.audioContext;
      if (contextAudio && contextAudio.state !== 'closed') {
        await contextAudio.close();
      }

      micContextMap.value[type] = null;
    }

    await new Promise((resolve) => setTimeout(resolve, 300)); // 컨텍스트 정리 대기
  };

  // 수동으로 Gain 조절
  const manualGainControll = (adjustGain) => {
    const gainValue = parseFloat(adjustGain);
    if (gainNode.value) {
      gainNode.value.gain.value = gainValue;
    } else {
      if (selectedInput.value) selectedInput.value.initialGain = gainValue;
    }
  };

  // 마이크 준비 상태 확인
  const isMicReady = (type: TypeInputMic = 'streaming') => {
    const ctx = micContextMap.value[type];
    if (!ctx || !ctx.audioContext) return false;
    return ctx.audioContext.state !== 'closed';
  };

  // Gain 적용 + 병합 채널 생성
  const createDestinationWithGainAdjusted = (stream, audioContext, input) => {
    gainNode.value = audioContext.createGain();

    const gain =
      input?.initialGain != null
        ? input.initialGain
        : (selectedInput.value?.initialGain ?? 1.0);

    console.log(`gain: ${gain}`);

    gainNode.value.gain.value = gain;

    try {
      const sourceNode = audioContext.createMediaStreamSource(stream);
      const destinationNode = audioContext.createMediaStreamDestination();
      const merger = audioContext.createChannelMerger(1);

      sourceNode.connect(gainNode.value);
      gainNode.value.connect(merger, 0, 0);
      merger.connect(destinationNode);
      return destinationNode;
    } catch (e) {
      console.error('No mic access authorization.');
      return null;
    }
  };

  if (typeof window !== 'undefined') {
    // 브라우저 창이 닫히거나 새로고침될 때 실행되는 이벤트 리스너를 추가합니다.
    window.addEventListener('beforeunload', () => {
      // 마이크와 관련된 모든 리소스를 정리하고 메모리 누수를 방지합니다.
      micClear('streaming'); // 스트리밍 마이크 초기화
      micClear('voice'); // 음성 마이크 초기화
      micClear('voice_streaming'); // 음성 마이크 초기화
    });
  }

  const monitorGain = () => {
    if (typeof window === 'undefined' || intervalGainMonitor) return;
    let previousGainValue = 0;

    intervalGainMonitor = setInterval(() => {
      const currentGain = gainNode.value?.gain?.value;
      if (
        typeof currentGain === 'number' &&
        currentGain !== previousGainValue
      ) {
        previousGainValue = currentGain;
        console.trace('[GAIN] 게인 값이 변했습니다');
        console.warn(
          `[GAIN] 이전 게인 값:${previousGainValue} 새로운 값: ${currentGain}`
        );
      }
    }, 500); // Check every 500ms (adjust if needed)
  };

  // 마이크 재기동
  const micReset = async () => {
    console.log('MIC RESET');
    await micClear('streaming');
    const success = await micPrep('streaming');
    if (success && isMicOn.value) {
      await micStart('streaming');
    } else {
      toastStore.error(t('reject_mic'));
    }
  };

  monitorGain();

  return {
    voiceRecordingResult,
    gainNode,
    micContextMap,
    micAccess,
    micPrep,
    micStart,
    micStop,
    micClear,
    micReset,
    manualGainControll,
    isMicReady,
    createDestinationWithGainAdjusted,
  };
}
